<template>
  <DModal :show-close="false" :isBgOverlay="true" :isCloseOnBgClick="false" @close="$emit('close')" :appendToBody="true"
          style="z-index: 12312312312312" :placeInTop="true">
    <div class="d-modal text-dark" slot="insertComponent">
      <div class="d-modal__header">
        <div>
          <h2>Automatisk indberetning</h2>
          <p class="d-modal__subtitle">
            Opretter fraværsindberetninger ud fra fravær registreret i Vikardækning i den valgte periode.
          </p>
        </div>
        <div class="font-large icon__bg-circle d-modal__header__close" @click="$emit('close')">
          <span class="icon-cross2"></span>
        </div>
      </div>

      <!-- Scroll kun i resultatvisningen: i valg-visningen skal datepickerens kalender kunne stikke ud af modalen -->
      <div class="d-modal__content" :class="{'d-modal__content--scroll': createdFravaer !== null}">
        <template v-if="createdFravaer === null">
          <div class="auto-period">
            <span class="bold">Periode:</span>
            <DoDatePickerRange
              :allowSameDate="true"
              :startDate="startDate"
              :endDate="endDate"
              :maximumDateRange="365"
              @startChanged="$emit('startChanged', $event)"
              @endChanged="$emit('endChanged', $event)">
            </DoDatePickerRange>
          </div>
          <p class="text-explainer">
            Fravær der allerede er indberettet, springes over. Åbenstående fravær indberettes uden slutdato —
            når fraværet senere afsluttes, oprettes en ny indberetning med den rigtige slutdato ved næste kørsel.
            Indberetningerne lægges i udbakken og sendes automatisk til SD.
          </p>
          <p v-if="errorMessage" class="text-explainer text-explainer--danger">{{ errorMessage }}</p>
        </template>

        <template v-else>
          <p v-if="createdFravaer.length === 0" class="italic">
            Der blev ikke fundet nyt fravær i perioden. Alt fravær er enten allerede indberettet eller mangler
            fraværskode/ansættelse.
          </p>
          <template v-else>
            <p>{{ createdFravaer.length }} {{ createdFravaer.length === 1 ? 'fraværsindberetning' : 'fraværsindberetninger' }} blev lagt i udbakken:</p>
            <table class="do-table">
              <thead>
              <tr>
                <th>NAVN</th>
                <th>FRAVÆRSTYPE</th>
                <th>FRA</th>
                <th>TIL</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="item in createdRows" :key="item.id">
                <td class="bold">{{ item.vUserName }}</td>
                <td>{{ item.vAbsenceType }}</td>
                <td>{{ item.vStartdato }}</td>
                <td>{{ item.vSlutdato }}</td>
              </tr>
              </tbody>
            </table>
          </template>
        </template>
      </div>

      <div class="d-modal__buttons">
        <div class="d-modal__buttons__child">
          <template v-if="createdFravaer === null">
            <button class="do-bttn" @click="$emit('close')">{{ $t('vue2.common.cancel') }}</button>
            <button class="do-bttn do-bttn--submit"
                    :class="{'do-bttn--submit--loading': isSubmitting, 'do-bttn--submit--disabled': !isValid}"
                    :disabled="!isValid || isSubmitting"
                    @click="submit">
              Hent og opret indberetninger
            </button>
          </template>
          <button v-else class="do-bttn do-bttn--submit" @click="finish">Luk</button>
        </div>
      </div>
    </div>
  </DModal>
</template>

<script>
import DModal from '../../common/DModal.vue'
import DoDatePickerRange from '../../datepicker/DoDatePickerRange.vue'
import moment from 'moment'
import docendo from '../../../docendo'

export default {
  name: 'SdAutoFravaer',
  components: {DModal, DoDatePickerRange},
  props: {
    schoolId: {
      type: Number,
      required: true
    },
    absenceCodes: {
      type: Array,
      default: () => []
    },
    // Perioden forudfyldes fra datovælgeren i toolbaren og kan justeres i modalen;
    // ændringer emittes tilbage til forælderen (startChanged/endChanged).
    startDate: {
      required: true
    },
    endDate: {
      required: true
    }
  },
  data () {
    return {
      isSubmitting: false,
      errorMessage: '',
      createdFravaer: null
    }
  },
  computed: {
    isValid () {
      return !!this.startDate && !!this.endDate &&
        moment(this.startDate).isValid() && moment(this.endDate).isValid() &&
        !moment(this.endDate).isBefore(moment(this.startDate), 'day')
    },
    usersById () {
      const map = {}
      const list = this.$store.getters.usersSortedOnName || []
      for (let i = 0; i < list.length; i++) map[list[i].id] = list[i].name
      return map
    },
    absenceCodesByLoenart () {
      const map = {}
      for (let i = 0; i < this.absenceCodes.length; i++) {
        const code = this.absenceCodes[i]
        if (code.loenart != null) map[String(parseInt(code.loenart, 10))] = code
      }
      return map
    },
    createdRows () {
      const list = this.createdFravaer || []
      return list.map(item => {
        const code = item.loenart != null ? this.absenceCodesByLoenart[String(parseInt(item.loenart, 10))] : null
        return Object.assign({}, item, {
          vUserName: (item.user_id && this.usersById[item.user_id]) || String(item.tjenestenummer || ''),
          vAbsenceType: code ? code.loenart + ' - ' + code.title : (item.loenart != null ? String(item.loenart) : ''),
          vStartdato: item.startdato ? moment(item.startdato).format('DD-MM-YYYY') : '',
          vSlutdato: this.formatSlutdato(item.slutdato)
        })
      })
    }
  },
  methods: {
    // Igangværende fravær uden slutdato gemmes i backenden med år 9999 og sendes til SD som '999999'.
    formatSlutdato (slutdato) {
      if (!slutdato) return ''
      const date = moment(slutdato)
      if (date.year() >= 9999) return 'Åbenstående'
      return date.format('DD-MM-YYYY')
    },
    submit () {
      if (!this.isValid || this.isSubmitting) return
      this.errorMessage = ''
      this.isSubmitting = true
      docendo.generateSilkeborgDataFravaer(
        this.schoolId,
        moment(this.startDate).format('YYYY-MM-DD'),
        moment(this.endDate).format('YYYY-MM-DD'),
        success => {
          this.isSubmitting = false
          if (success && success.status === 'error') {
            this.errorMessage = 'Kunne ikke oprette fraværsindberetningerne'
            return
          }
          this.createdFravaer = Array.isArray(success) ? success : (success && Array.isArray(success.data) ? success.data : [])
        },
        failure => {
          console.log('generate fravaer failure', failure)
          this.isSubmitting = false
          this.errorMessage = 'Kunne ikke oprette fraværsindberetningerne'
        }
      )
    },
    finish () {
      this.$emit('created', this.createdFravaer || [])
      this.$emit('close')
    }
  },
  mounted () {
    this.didAddNoScroll = !document.body.classList.contains('no-scroll')
    if (this.didAddNoScroll) {
      document.body.classList.add('no-scroll')
    }
  },
  beforeDestroy () {
    if (this.didAddNoScroll) {
      document.body.classList.remove('no-scroll')
    }
  }
}
</script>

<style scoped>
@import "../../../assets/css/variables.css";

.d-modal {
  width: 600px;
  max-width: calc(100vw - 32px);
  border-radius: 16px;
  position: relative;
}

.d-modal__header {
  margin-bottom: 0;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.d-modal__subtitle {
  margin: 0.25rem 0 0;
  font-size: 13px;
  color: #6b7280;
}

.d-modal__content {
  padding: 1rem;
  overscroll-behavior: contain;
}

.d-modal__content--scroll {
  max-height: calc(100vh - 52px - 80px);
  overflow-y: auto;
}

.d-modal__buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem 2rem;
  border-top: 1px solid #eee;
  margin: 0;
}

.d-modal__buttons__child {
  display: flex;
  gap: 0.5rem;
}

.auto-period {
  margin: 0 0 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.text-explainer--danger {
  color: var(--danger);
  margin-top: 0.5rem;
}

.do-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.5rem;
}

.do-table thead th {
  text-transform: uppercase;
  font-size: 10px;
  color: #8898aa;
  text-align: left;
  padding: 0.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.do-table tbody td {
  padding: 0.5rem;
  border-bottom: 1px solid #f1f5f9;
}
</style>



// WEBPACK FOOTER //
// SdAutoFravaer.vue?74e1347a